import { Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { TealCompReferral } from '../../../models/teal-comp-referral';
import { CommonModule, DatePipe, NgIf } from '@angular/common';
import { AccordionModule } from 'primeng/accordion'
import { InputTextModule } from 'primeng/inputtext'
import { FloatLabelModule } from 'primeng/floatlabel'
import { ButtonModule } from 'primeng/button';
import { SplitButtonModule } from 'primeng/splitbutton'
import { DividerModule } from 'primeng/divider'
import { FormsModule } from '@angular/forms';
import { IParty, Party, PartyInsert } from '@harlowe/core';
import { PartyEditorComponent } from '../../parties/party-editor/party-editor.component';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { PartyService } from '@harlowe/core';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { CardModule } from 'primeng/card';
import { ITealCompClaim, TealCompClaim, TealCompClaimInsert } from '../../../models/teal-comp-claim';
import { Store } from '@ngxs/store';
import { DropdownModule } from 'primeng/dropdown';
import { ClaimEditorComponent } from '../../claims/claim-editor/claim-editor.component';
import { ClaimService } from '../../../services/claim.service';
import { ProcedureSelectorComponent, FacilityLocationSearchComponent, PartySearchComponent } from '@harlowe/core';
import { Procedure } from '@harlowe/core';
import { ChipModule } from 'primeng/chip'
import { RadioButtonModule } from 'primeng/radiobutton'
import { bootstrapCheckCircleFill } from '@ng-icons/bootstrap-icons';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ButtonGroupModule } from 'primeng/buttongroup';
import { SubmissionViewerComponent } from '../submission-viewer/submission-viewer.component';
import { PanelModule } from 'primeng/panel';
import { AutoComplete } from 'primeng/autocomplete';
import { DialogModule } from 'primeng/dialog';
import { MenuModule } from 'primeng/menu';
import { BlockableUI, MenuItem } from 'primeng/api';
import { UtilService } from '@harlowe/core';
import { FileListComponent, IUploadProcessor } from '../../files/file-list/file-list.component';
import { ReferralService } from '../../../services/referral.service';
import { EncounterService, FileUpload } from '../../../services/encounter.service';
import { TealCompEncounterDetails } from '../../../models/encounter';
import { SentEmailListComponent, EncounterNotesListComponent, ReceivedEmailListComponent, DiaryListComponent, EmailService, HarloweSupport } from '@harlowe/core';
import { TabViewModule } from 'primeng/tabview';
import { EncounterDiaryHandler } from '../../encounter/encounter-editor/encounter-diary-handler';
import { EncounterEmailHandler } from '../../encounter/encounter-editor/encounter-email-handler';
import { EncounterNoteHandler } from '../../encounter/encounter-editor/encounter-note-handler';
import { plainToInstance } from 'class-transformer';

@Component({
  selector: 'app-referral-editor',
  standalone: true,
  imports: [NgIf,AccordionModule,InputTextModule, FloatLabelModule, 
            PartySearchComponent, ButtonModule,FormsModule, NgIf,
            DividerModule,PartyEditorComponent,OverlayPanelModule,NgIconComponent,
            CardModule,DatePipe,DropdownModule, ClaimEditorComponent,
            ProcedureSelectorComponent,CommonModule,ChipModule,RadioButtonModule,
            InputTextareaModule, ButtonGroupModule, FacilityLocationSearchComponent,
            SubmissionViewerComponent, PanelModule, DialogModule, SplitButtonModule,
            MenuModule, FileListComponent, SentEmailListComponent, EncounterNotesListComponent,
            ReceivedEmailListComponent, TabViewModule, DiaryListComponent
          ],
  templateUrl: './referral-editor.component.html',
  styleUrl: './referral-editor.component.scss',
  providers:[provideIcons({ bootstrapCheckCircleFill })]
})
export class ReferralEditorComponent
{

  @Input() set referral(value: TealCompReferral | null | undefined)
  {
    if(!value)
    {
      //clear other stuff if the referral is empty
      this.resetForm();
    }
    else
    {
      this.selectedReferral = structuredClone(value);
      this.selectedReferral.procedures = value.procedures as Procedure[];
      this.selectedParty = this.selectedReferral.claim?.party;
      this.loadExistingClaimsForSelectedParty();
    
      this.selectedReferral.submitterJobFunction = this.selectedReferral.submission.submitterJobFunction;
      //Set the attachments list
      this.attachments = value.attachments?.map(a => {return {label: a.fileName, icon:"pi pi-file-pdf", command: () =>  this.viewAttachment(a)  }})
      if(value)
        this.uploader = new ReferralFileUploader(value.id,this._referralService);

    }
  }

  private _encounter: TealCompEncounterDetails | null | undefined;

  @Input() set encounter (value : TealCompEncounterDetails | null | undefined)
  {
    this._encounter = value
    if(value)
    {
      this.encounterDiaryHandler = new EncounterDiaryHandler(value,this._encounterService,this._store);
      this.encounterNoteHandler = new EncounterNoteHandler(value,this._encounterService,this._store);
      this.initializeEmailHandler();
    }
  }

  get encounter(): TealCompEncounterDetails | null | undefined 
  {  
    return this._encounter;
  }

  @Output() saved: EventEmitter<TealCompReferral> = new EventEmitter<TealCompReferral>();
  @Output() cancelled = new EventEmitter();


  encounterDiaryHandler? : EncounterDiaryHandler | undefined;
  encounterNoteHandler? : EncounterNoteHandler | undefined;

  selector?: AutoComplete;

  uploader?: IUploadProcessor;

  selectedReferral?: TealCompReferral;
  selectedParty?: Party;
  existingClaims?: TealCompClaim[];
  selectedProcedure?: Procedure;
  submitterTypes: string[] = ["Adjuster","Referring Provider","Nurse Case Manager","Other"];

  submissionVisible: boolean = false;

  newParty?: IParty = undefined;
  newClaim?: ITealCompClaim = undefined;

  attachments: MenuItem[] = [];

  emailDialogVisible: boolean = false;

  encounterEmailHandler: EncounterEmailHandler | undefined;


  constructor
  (
    private _partyService : PartyService,
    private _claimService : ClaimService,
    private _utilService: UtilService,
    private _referralService: ReferralService,
    private _encounterService: EncounterService,
    private _emailService: EmailService,
    private _store: Store,
  )
  {
  }

  private async initializeEmailHandler(): Promise<void> {
    this.encounterEmailHandler = await new EncounterEmailHandler(this.encounter as TealCompEncounterDetails, this._emailService, this._encounterService, this._referralService, this._claimService, this._store).create(1);
  }

  resetForm()
  {
    this.selectedParty = undefined;
    this.existingClaims = undefined;
    this.selectedReferral = undefined;
    this.selectedProcedure = undefined;
    this.newParty = undefined;
    this.newClaim = undefined;
  }

  partySelected(event:Party)
  {
    this.selectedParty = event;
    this.loadExistingClaimsForSelectedParty();
  }

  loadExistingClaimsForSelectedParty()
  {
    if(this.selectedParty != null)
    {
      //Find claims for this party
      this._claimService.getClaimsForParty(this.selectedParty.id)
      .then
      (
        claims => claims.length > 0 ? this.existingClaims = claims : undefined
      );
    }
  }

  partyCreated(party: IParty)
  {
    //Create the party now
    this._partyService.createNewParty
    (
      new PartyInsert(party)
    )
    .then
    (
      p => this.selectedParty = p
    );
  }

  partyEdited(id: number, party: IParty)
  {
    this._partyService.updateParty(id, {id:id, dateOfBirth:party.dateOfBirth, firstName:party.firstName, lastName:party.lastName,middleInitial: party.middleInitial})
    .then
    (
      p => this.selectedParty = p
    )
  }

  claimCreated(claim: ITealCompClaim)
  {
    //Set the party
    if(this.selectedParty != null)
      claim.party = this.selectedParty;
    
    let ci = new TealCompClaimInsert(claim.claimNumber,claim.party.id,claim.claimDate);
    ci.accidentDescription = claim.accidentDescription;
    ci.adjusterId = claim.adjuster?.id;
    ci.carrierId = claim.carrier?.id;
    ci.injuryDescription = claim.injuryDescription;
    ci.bodyPart = claim.bodyPart;

    //Commit the claim

      this._claimService.createClaim(ci)
      .then
      (
        c => 
          {
            if(this.selectedReferral != null)
              this.selectedReferral.claim = c
          }
      )
    
  }
  claimEdited(id: number, claim: ITealCompClaim)
  {

      this._claimService.updateClaim(id, {id: id,carrierId:claim.carrier?.id, accidentDescription:claim.accidentDescription, adjusterId: claim.adjuster?.id, adjuster2Id: claim.adjuster2?.id, adjusterSupervisorId: claim.adjusterSupervisor?.id, claimDate: claim.claimDate,claimNumber:claim.claimNumber,injuryDescription:claim.injuryDescription,lob:"Work_Comp",partyId:claim.party.id,bodyPart:claim.bodyPart})
      .then
      (
        c =>    
          { 
            if(this.selectedReferral)
              this.selectedReferral.claim = c
          }
      )
    }
  


  partyCreationCancelled()
  {
    
  }

  procedureSelected(event: Procedure)
  {
    this.selectedProcedure = event;
  }


  addSelectedProcedure(event: MouseEvent)
  {

    
    if(this.selectedReferral != null &&  this.selectedProcedure != null && (this.selectedReferral?.procedures == null || !this.selectedReferral?.procedures.map(p => p.code).includes(this.selectedProcedure.code)))
    {
      if(this.selectedReferral?.procedures == null)
        {
          this.selectedReferral.procedures = [];
        }

      this.selectedReferral.procedures = [...this.selectedReferral.procedures,this.selectedProcedure];
    }
  }
  removeProcedure(event: Procedure)
  {
    if(this.selectedReferral)
      this.selectedReferral.procedures = this.selectedReferral?.procedures.filter(p => p.id != event.id);
  }


  submitterInfoComplete() : boolean
  {
    return (this.selectedReferral?.submitterName ?? '') != '' && 
      ((this.selectedReferral?.submitterPhone ?? '') != ''  || (this.selectedReferral?.submitterEmail ?? '') != '') &&
      ((this.selectedReferral?.submitterJobFunction ?? '') != '')
  }

  allowSave() : boolean
  {
    return  this.selectedReferral != null && 
            this.selectedReferral?.claim != null && 
            this.selectedReferral.procedures != null && 
            this.selectedReferral.procedures.length > 0 && 
            this.selectedReferral.referringProvider != null &&
            this.selectedReferral.servicingProvider != null
            this.submitterInfoComplete();
  }

  saveButtonPressed(event: MouseEvent)
  {
    //Update this to reviewed if the status was new
    if(this.selectedReferral?.status == 'New' || this.selectedReferral?.status == 'SubmittedForApproval')
      this.selectedReferral.status = 'Reviewed'
    this.saved.emit(this.selectedReferral);
  }

  cancelButtonPressed(event: MouseEvent)
  {
    this.cancelled.emit();
  }

  fillFromSubmission()
  {
    if(this.selectedReferral)
    {

      this.selectedReferral.submitterName = this.selectedReferral.submission.submittedByFirstName + " " + this.selectedReferral.submission.submittedByLastName;
      this.selectedReferral.submitterEmail = this.selectedReferral.submission.submitterEmail;
      this.selectedReferral.submitterPhone = this.selectedReferral.submission.submitterPhone;
      this.selectedReferral.submitterJobFunction = this.selectedReferral.submission.submitterJobFunction;
      this.selectedReferral.specialInstructions = this.selectedReferral.submission.specialInstructions;
    }
  }

  clearForm()
  {
    if(this.selectedReferral)
    {
      this.selectedReferral.submitterName = undefined;
      this.selectedReferral.submitterEmail = undefined;
      this.selectedReferral.submitterPhone = undefined;
      this.selectedReferral.submitterJobFunction = undefined;
    }
  }

  async viewAttachment(file:HarloweSupport.File)
  {
    var blob = await this._utilService.getFileBlob(file);
    let url = window.URL.createObjectURL(blob);
      let pwa = window.open(url);
  }

  attachmentsChanged(files: HarloweSupport.File[])
  {
    if(this.selectedReferral)
    {
      this.selectedReferral.attachments = [...files]; 
      this.encounterEmailHandler?.updateAttachments("Referral", files);
    }
  }
}

export class ReferralFileUploader implements IUploadProcessor
{


  constructor(private referralId: number, private _referralService: ReferralService)
  {

  }

  async deleteFile(file: HarloweSupport.File): Promise<HarloweSupport.File> 
  {
    var deletedFile = await this._referralService.deleteAttachment(this.referralId,file.id);
    return deletedFile;
  }

  async uploadFiles(files: FileUpload[]): Promise<HarloweSupport.File[]> 
  {
    if(files && files.length > 0)
    {
      var referralFiles = await this._referralService.addAttachments(this.referralId,files);
      return referralFiles.map(f => plainToInstance(HarloweSupport.File,f));
    }
    return [];
  }
}
